// src/firebase/users.js
import { 
    getFirestore, 
    collection, 
    doc, 
    setDoc, 
    getDoc, 
    updateDoc,
    serverTimestamp 
  } from 'firebase/firestore';
  import { getAuth, onAuthStateChanged } from 'firebase/auth';
  
  const db = getFirestore();
  const USERS_COLLECTION = 'examContentUsers';
  
  // Create or update a user in the users collection
  export const createOrUpdateUser = async (user) => {
    if (!user) return null;
    
    try {
      const userRef = doc(db, USERS_COLLECTION, user.uid);
      const userSnapshot = await getDoc(userRef);
      
      if (!userSnapshot.exists()) {
        // Create new user document
        await setDoc(userRef, {
          uid: user.uid,
          displayName: user.displayName || '',
          email: user.email || '',
          photoURL: user.photoURL || user.customAvatarUrl || '',
          title: user.title || '',
          phoneNumber: user.phoneNumber || '',
          role: 'user', // Default role
          lastLogin: serverTimestamp(),
          createdAt: serverTimestamp(),
          appSource: 'exam-content',
          platform: 'cosknow',
          receiveTextAlerts: !!user.phoneNumber // Enable alerts if phone number provided
        });
      } else {
        // Update existing user document
        const updates = {
          displayName: user.displayName || userSnapshot.data().displayName,
          email: user.email || userSnapshot.data().email,
          lastLogin: serverTimestamp()
        };
        
        // Only update optional fields if they are provided
        if (user.photoURL) updates.photoURL = user.photoURL;
        if (user.customAvatarUrl) updates.photoURL = user.customAvatarUrl;
        if (user.title) updates.title = user.title;
        if (user.phoneNumber) {
          updates.phoneNumber = user.phoneNumber;
          updates.receiveTextAlerts = true;
        }
        
        await updateDoc(userRef, updates);
      }
      
      // Return the user data
      const updatedUserSnapshot = await getDoc(userRef);
      return {
        ...updatedUserSnapshot.data(),
        id: updatedUserSnapshot.id
      };
    } catch (error) {
      console.error("Error creating/updating user:", error);
      throw error;
    }
  };
  
  // Get a user by ID
  export const getUserById = async (userId) => {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      const userSnapshot = await getDoc(userRef);
      
      if (!userSnapshot.exists()) {
        return null;
      }
      
      return {
        ...userSnapshot.data(),
        id: userSnapshot.id
      };
    } catch (error) {
      console.error("Error getting user:", error);
      throw error;
    }
  };
  export const isUserAdmin = async (userId) => {
    try {
      const userData = await getUserById(userId);
      return userData?.role === 'admin';
    } catch (error) {
      console.error("Error checking admin status:", error);
      return false;
    }
  };
  
  // Admin function to update a user's role
  export const updateUserRole = async (userId, newRole, adminUserId) => {
    try {
      // Verify the requester is an admin
      const isAdmin = await isUserAdmin(adminUserId);
      if (!isAdmin) {
        throw new Error("Unauthorized: Only admins can update user roles");
      }
      
      // Update the user's role
      const userRef = doc(db, USERS_COLLECTION, userId);
      await updateDoc(userRef, {
        role: newRole
      });
      
      return true;
    } catch (error) {
      console.error("Error updating user role:", error);
      throw error;
    }
  };
  // User authentication hook
  export const useAuth = (callback) => {
    const auth = getAuth();
    
    onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        try {
          // Create or update user in Firestore
          const userData = await createOrUpdateUser(authUser);
          callback(userData);
        } catch (error) {
          console.error("Error in auth state change:", error);
          callback(null);
        }
      } else {
        callback(null);
      }
    });
    
    return () => {}; // Cleanup function
  };